import { useQuery } from '@tanstack/react-query';
import { Activity, AlertTriangle, Calendar, Clock, FileSignature, Library } from 'lucide-react';

import { Card, EmptyState } from '@axioma/ui';

import { KpiTile } from '../components/KpiTile.js';
import { PageHeader } from '../components/PageHeader.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/auth.js';

interface TaskListResponse {
  items: Array<{
    id: string;
    title: string;
    actionType: string;
    legalDeadlineAt: string;
    deadlineState: string;
    urgencyTier: string;
    process: { cnjNumberMasked: string };
  }>;
  total: number;
}

export function Dashboard(): JSX.Element {
  const tenant = useAuthStore((s) => s.tenants.find((t) => t.id === s.currentTenantId));

  const tasksQuery = useQuery({
    queryKey: ['dashboard', 'tasks', tenant?.id],
    queryFn: async () => {
      const { data } = await api.get<TaskListResponse>('/vigilia/tasks', {
        params: { pageSize: 5, deadlineState: 'RED' },
      });
      return data;
    },
    enabled: Boolean(tenant?.id),
  });

  return (
    <div>
      <PageHeader
        eyebrow="Visão geral"
        title={`Bem-vindo, ${useAuthStore.getState().user?.name?.split(' ')[0] ?? ''}.`}
        description={
          <>
            <span className="ax-editorial italic text-ax-text-secondary">
              Antes da prova, o axioma.
            </span>{' '}
            Workspace ativo:{' '}
            <strong className="text-ax-text-primary font-medium">{tenant?.name ?? '—'}</strong>
          </>
        }
      />

      {/* KPI strip */}
      <section className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 px-8 py-6">
        <KpiTile label="Vencidas" value="0" variant="critical" hint="estado deve ser 0" />
        <KpiTile label="Crítica < 48h" value={String(tasksQuery.data?.total ?? 0)} variant="aurora" hint="prazo legal próximo" />
        <KpiTile label="Em produção" value="—" hint="pipelines IA ativos" />
        <KpiTile label="Aguardando assinatura" value="—" hint="peças prontas" />
        <KpiTile label="Sources monitoradas" value="—" hint="MNI · eproc · DJEN" />
        <KpiTile label="Custo IA · mês" value="$ —" hint="orçamento de tokens" />
      </section>

      {/* Tarefas críticas */}
      <section className="px-8 pb-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-xl text-ax-text-primary tracking-tight">
            Próximas tarefas críticas
          </h2>
          <a href="/agenda" className="font-mono text-[11px] text-ax-aurora uppercase tracking-wider hover:text-ax-aurora-glow">
            Ver agenda completa →
          </a>
        </div>

        {!tasksQuery.data || tasksQuery.data.items.length === 0 ? (
          <EmptyState
            icon={<Calendar size={32} />}
            title="Nenhuma tarefa crítica neste momento"
            description="Cadastre processos no Vigília para começar a monitorar prazos e movimentos."
          />
        ) : (
          <div className="space-y-2">
            {tasksQuery.data.items.map((t) => (
              <Card key={t.id} accentLeft="critical" interactive>
                <div className="px-5 py-3 flex items-center gap-4">
                  <Clock size={16} className="text-ax-critical animate-ax-pulse-critical" />
                  <div className="flex-1 min-w-0">
                    <p className="font-display text-sm text-ax-text-primary truncate">{t.title}</p>
                    <p className="font-mono text-[11px] text-ax-text-tertiary truncate">
                      {t.process.cnjNumberMasked} · {t.actionType}
                    </p>
                  </div>
                  <p className="font-mono text-xs text-ax-critical uppercase tracking-wider">
                    {new Date(t.legalDeadlineAt).toLocaleDateString('pt-BR')}
                  </p>
                </div>
              </Card>
            ))}
          </div>
        )}
      </section>

      {/* Atalho 5 visões */}
      <section className="px-8 pb-8">
        <h2 className="font-display text-xl text-ax-text-primary tracking-tight mb-3">
          As 5 visões do Vigília
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <ShortcutCard href="/agenda" icon={<Calendar size={18} />} label="Agenda crítica" hint="g a" />
          <ShortcutCard href="/kanban" icon={<Activity size={18} />} label="Kanban" hint="g k" />
          <ShortcutCard href="/carteira" icon={<Library size={18} />} label="Carteira" hint="g c" />
          <ShortcutCard href="/risco" icon={<AlertTriangle size={18} />} label="Risco" hint="g r" />
          <ShortcutCard href="/processos" icon={<FileSignature size={18} />} label="Processos" hint="g p" />
        </div>
      </section>
    </div>
  );
}

function ShortcutCard({
  href,
  icon,
  label,
  hint,
}: {
  href: string;
  icon: React.ReactNode;
  label: string;
  hint: string;
}): JSX.Element {
  return (
    <a
      href={href}
      className="group flex flex-col gap-2 p-4 rounded-ax-lg border border-ax-line bg-ax-graphite hover:bg-ax-night hover:border-[color:rgb(58_61_70)] transition-all"
    >
      <span className="text-ax-aurora group-hover:text-ax-aurora-glow transition-colors">{icon}</span>
      <p className="font-display text-sm text-ax-text-primary tracking-tight">{label}</p>
      <p className="font-mono text-[10px] text-ax-text-tertiary uppercase tracking-wider">{hint}</p>
    </a>
  );
}
